import { type FC, type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Check from '@assets/img/icons/check.svg?react';

import { useChangeAdditionalParamsMutation } from '@/api/queries/user';
import { getFullName } from '@/common/lib/getFullName';
import { H2 } from '@/components/Typography/Typography';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { updateFirstName } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

// Совпадает с @Length(1, 32) в UserSettingsDto на бэкенде
const MAX_NAME_LENGTH = 32;

const PlayerName: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const [changeAdditionalParams, { isLoading }] =
    useChangeAdditionalParamsMutation();
  const [draft, setDraft] = useState<string | null>(null);

  const name = user ? (getFullName(user) ?? '') : '';

  const save = async (e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    const firstName = draft?.trim() ?? '';

    if (!firstName || firstName === name) {
      setDraft(null);

      return;
    }

    try {
      await changeAdditionalParams({ firstName }).unwrap();
      dispatch(updateFirstName(firstName));
      setDraft(null);
    } catch {
      showToast(t('Something went wrong, please try again'));
    }
  };

  if (draft !== null) {
    return (
      <form
        className="flex items-center gap-2 mb-1 w-full max-w-[16rem]"
        onSubmit={(e) => void save(e)}
      >
        <input
          autoFocus
          value={draft}
          maxLength={MAX_NAME_LENGTH}
          disabled={isLoading}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && setDraft(null)}
          className="flex-1 min-w-0 h-9 px-3 rounded-lg bg-white/10 text-white text-center text-lg font-bold outline-none focus:ring-1 focus:ring-white/40"
        />
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center justify-center size-9 shrink-0 rounded-lg bg-button-green-gradient disabled:opacity-50"
        >
          <Check className="size-5" />
        </button>
      </form>
    );
  }

  return (
    <button
      type="button"
      className="flex items-center gap-1.5 mb-1 max-w-full"
      onClick={() => setDraft(name)}
    >
      <H2 weight="bold" className="truncate">
        {name}
      </H2>
      <svg
        viewBox="0 0 24 24"
        className="size-4 shrink-0 text-white/40"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    </button>
  );
};

export { PlayerName };
